import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { ChatHistoryItemProps } from '@/types/components/converse/history';
// import { formatDistanceToNow } from 'date-fns';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import IosShareIcon from '@mui/icons-material/IosShare';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PushPinIcon from '@mui/icons-material/PushPin';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import React, { useEffect, useMemo, useState } from 'react';
import ShareModal from './ShareModal';
import { CircularProgress } from '@mui/material';

// Simple polyfill for date-fns functionality
const formatDistanceToNow = (
  date: Date,
  options?: { addSuffix: boolean }
): string => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return options?.addSuffix ? 'just now' : 'now';

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60)
    return options?.addSuffix
      ? `${diffInMinutes} minutes ago`
      : `${diffInMinutes} minutes`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24)
    return options?.addSuffix
      ? `${diffInHours} hours ago`
      : `${diffInHours} hours`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30)
    return options?.addSuffix ? `${diffInDays} days ago` : `${diffInDays} days`;

  const diffInMonths = Math.floor(diffInDays / 30);
  return options?.addSuffix
    ? `${diffInMonths} months ago`
    : `${diffInMonths} months`;
};

/**
 * ChatHistoryItem component
 *
 * Displays a single chat history item with title and timestamp.
 * Handles selection and clicking to load the chat.
 *
 * @param chat - Chat history object to display
 * @param currentChatID - ID of the currently selected chat
 * @param setCurrentChatID - Callback to set the current chat ID on selection
 */
function ChatHistoryItem({
  chat,
  currentChatID,
  setCurrentChatID,
  refetchHistory,
  page,
}: ChatHistoryItemProps): React.ReactNode {
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [fetchingSessionId, setFetchingSessionId] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState(chat.title);
  const [deletingChat, setDeletingChat] = useState(false);
  const [isPinned, setIsPinned] = useState(chat.pinned || false);
  const [pinningChat, setPinningChat] = useState(false);

  const menuOpen = Boolean(menuAnchorEl);

  const {
    setStoreState,
    getChatState,
    updateChatWithDiff,
    chatRecordings,
    alchemystChat,
    snackbarEntries,
    pinnedCount,
    chatChannel,
  } = useAlchemystStoreForAi((state) => ({
    setStoreState: state.setStoreState,
    getChatState: state.getChatState,
    updateChatWithDiff: state.updateChatWithDiff,
    chatRecordings: state.chatRecordings,
    alchemystChat: state.chat,
    snackbarEntries: state.snackbarEntries,
    pinnedCount: state.pinnedCount,
    chatChannel: state.chatChannel,
  }));

  // Check if this chat has recordings
  const hasRecording = useMemo(() => {
    const recording = chatRecordings[chat.id];
    return (
      !!recording &&
      ((Array.isArray(recording.rrwebEvents) &&
        recording.rrwebEvents.length > 0) ||
        (Array.isArray(recording.snapshots) && recording.snapshots.length > 0))
    );
  }, [chatRecordings, chat.id]);

  // When the chat is selected, update the chat recordings
  useEffect(() => {
    if (currentChatID === chat.id) {
      // If this is the current chat, store its state for future diff tracking
      // Only do this if we don't already have a state for this chat
      const existingChatState = getChatState(chat.id);
      if (!existingChatState) {
        // Initialize with first state
        updateChatWithDiff(chat.id, {
          id: chat.id,
          title: chat.title,
          timestamp: chat.timestamp,
          messages: [],
        });
      }
    }
  }, [currentChatID, chat.id]);

  // Function to handle chat selection
  const handleChatSelection = () => {
    setCurrentChatID(chat.id);
    setStoreState({
      chat: {
        ...alchemystChat,
        tools: { ...alchemystChat.tools, researchIcps: false },
      },
    });
  };

  // Function to handle menu open
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent chat selection
    setMenuAnchorEl(event.currentTarget);
  };

  // Function to handle menu close
  const handleMenuClose = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // Prevent chat selection
    setMenuAnchorEl(null);
  };

  // Function to handle share button click
  const handleShare = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent chat selection
    setMenuAnchorEl(null);

    if (!hasRecording) return;

    try {
      setFetchingSessionId(true);

      const queryParams = new URLSearchParams({
        chatId: 'true',
      });

      // First enable sharing of conversation.
      const sharingConfirmationResponse = await fetchWithRewrites(
        `/api/v1/replaysession/${chat.id}/share/?${queryParams}`
      );

      if (!sharingConfirmationResponse.ok) {
        throw new Error('Failed to enable sharing of conversation');
      }

      const res = await fetchWithRewrites(
        `/api/v1/replaysession/${chat.id}?${queryParams}`
      );

      if (!res.ok) {
        console.error('Failed to fetch session ID:', res.statusText);
        throw new Error('Failed to fetch session ID');
      }
      const data = await res.json();
      const newSessionId = data.data._id;
      setSessionId(newSessionId);
      setStoreState({
        chat: {
          ...alchemystChat,
          replaySessionID: newSessionId,
        },
      });
      setShareModalOpen(true);
    } catch (error) {
      console.error('Error fetching session ID:', error);
    } finally {
      setFetchingSessionId(false);
    }
  };

  // Function to handle delete button click
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent chat selection
    setMenuAnchorEl(null);
    setDeleteDialogOpen(true);
  };


  // Function to handle pin/unpin
  const handlePinToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent chat selection
    setMenuAnchorEl(null);

    try {
      setPinningChat(true);
      const newPinnedState = !isPinned;

      const response = await fetchWithRewrites(`/api/v1/chat/${chat.id}/pin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ pinned: newPinnedState }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${newPinnedState ? 'pin' : 'unpin'} chat`);
      }

      setIsPinned(newPinnedState);

      if (refetchHistory && page) {
        refetchHistory({ channel: false }, page);
      }

      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: `Chat ${newPinnedState ? 'pinned' : 'unpinned'} successfully`,
            severity: 'success',
            notificationId: crypto.randomUUID(),
            type: 'alert',
          },
        ],
      });
    } catch (error) {
      console.error('Error toggling pin status:', error);
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: `Failed to ${isPinned ? 'unpin' : 'pin'} chat`,
            severity: 'error',
            notificationId: crypto.randomUUID(),
            type: 'alert',
          },
        ],
      });
    } finally {
      setPinningChat(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeletingChat(true);
      const response = await fetchWithRewrites(
        `/api/v1/chat/delete/${chat.id}`,
        {
          method: 'DELETE',
        }
      );

      if (response.status !== 204) {
        setStoreState({
          snackbarEntries: [
            ...snackbarEntries,
            {
              message: 'Failed to delete chat',
              severity: 'error',
              notificationId: crypto.randomUUID(),
              type: 'alert',
            },
          ],
        });
      }

      if (currentChatID === chat.id) {
        setCurrentChatID('');
      }

      if (refetchHistory && page) refetchHistory({ channel: false }, page);
    } catch (error) {
      console.error('Error deleting chat:', error);
      setStoreState({
        snackbarEntries: [
          ...snackbarEntries,
          {
            message: 'Failed to delete chat',
            severity: 'error',
            notificationId: crypto.randomUUID(),
            type: 'alert',
          },
        ],
      });
    } finally {
      setDeletingChat(false);
      setDeleteDialogOpen(false);
    }
  };


  return (
    <>
      <Paper
        sx={(theme) => ({
          borderLeft:
            currentChatID === chat.id ? '4px solid #ff9a35' : 'transparent',
          borderRadius: '4px',
          padding: '12px',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'primary.paper',
          },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        })}
        onClick={handleChatSelection}
      >
        <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 'bold',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'text.primary',
              }}
              variant="subtitle2"
            >
              {chat.title}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              color="text.secondary"
              sx={{ fontSize: 'small' }}
              variant="caption"
            >
              {chat.timestamp
                ? formatDistanceToNow(new Date(chat.timestamp), {
                    addSuffix: true,
                  })
                : 'Unknown time'}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {isPinned && (
            <Tooltip title="Pinned">
              <PushPinIcon
                fontSize="small"
                sx={{
                  color: '#ff9a35',
                  marginRight: '4px',
                  transform: 'rotate(45deg)',
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Options">
            <IconButton
              onClick={handleMenuClick}
              size="small"
              sx={{
                color: currentChatID === chat.id ? '#ff9a35' : '#757575',
                '&:hover': {
                  color: currentChatID === chat.id ? '#ffb668' : '#9e9e9e',
                  backgroundColor:
                    currentChatID === chat.id
                      ? 'rgba(255, 154, 53, 0.1)'
                      : 'transparent',
                },
                padding: '4px',
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={menuAnchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            onClick={(e) => e.stopPropagation()}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {chatChannel !== 'integrations.whatsapp' &&
              (isPinned || pinnedCount < 3) && (
                <MenuItem onClick={handlePinToggle} disabled={pinningChat}>
                  <ListItemIcon>
                    <PushPinIcon
                      fontSize="small"
                      sx={{ transform: isPinned ? 'rotate(45deg)' : 'none' }}
                    />
                  </ListItemIcon>
                  <ListItemText>{isPinned ? 'Unpin' : 'Pin'}</ListItemText>
                </MenuItem>
              )}

            {pinningChat && <CircularProgress size={16} sx={{ ml: 1 }} />}
            <MenuItem
              onClick={handleShare}
              disabled={!hasRecording || fetchingSessionId}
            >
              <ListItemIcon>
                <IosShareIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Share</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDeleteClick}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Paper>

      {/* Share Modal */}
      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        sessionId={sessionId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>Delete Chat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this chat? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>

          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={deletingChat}
            autoFocus
          >
            {deletingChat ? <CircularProgress size={18} /> : <p>Delete</p>}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ChatHistoryItem;
